import * as THREE from 'three'

import type { Bsp } from './bsp'
import { Wad, type DecodedTexture } from './wad'

/** Tool textures that must never be rendered. */
const SKIP = new Set([
  'aaatrigger',
  'clip',
  'clipbevel',
  'null',
  'hint',
  'skip',
  'origin',
  'bevel',
  'nodraw',
  'sky',
  'translucent',
  'contentwater', // handled as world brush, but the marker face is invisible
])

const UPSCALE_TARGET = 512
const UPSCALE_MAX = 1024

export interface StoreEntry {
  texture: THREE.Texture
  transparent: boolean
  width: number
  height: number
  /** True when the texture could not be found and a placeholder is used. */
  missing: boolean
}

/**
 * Resolves a texture name to a GPU texture, pulling from the BSP's embedded
 * textures first and falling back to the loaded WADs. Decoded textures and the
 * resulting `THREE.Texture` objects are cached.
 */
export class TextureStore {
  private readonly decoded = new Map<string, DecodedTexture>()
  private readonly entries = new Map<string, StoreEntry | null>()
  private fallback?: StoreEntry

  constructor(
    bsp: Bsp,
    private readonly wads: Wad[],
  ) {
    for (const tex of bsp.textures) {
      if (tex.embedded) this.decoded.set(tex.name.toLowerCase(), tex.embedded)
    }
  }

  static shouldSkip(name: string): boolean {
    return name.length === 0 || SKIP.has(name.toLowerCase())
  }

  /** Sky faces get a dedicated skip so a real skybox can be added later. */
  static isSky(name: string): boolean {
    return name.toLowerCase() === 'sky'
  }

  private resolve(name: string): DecodedTexture | null {
    const key = name.toLowerCase()
    const cached = this.decoded.get(key)
    if (cached) return cached
    for (const wad of this.wads) {
      const tex = wad.getTexture(key)
      if (tex) {
        this.decoded.set(key, tex)
        return tex
      }
    }
    return null
  }

  private makeTexture(dec: DecodedTexture): THREE.Texture {
    const enhanced = upscaleTexture(dec)
    const tex = new THREE.DataTexture(
      enhanced.rgba,
      enhanced.width,
      enhanced.height,
      THREE.RGBAFormat,
    )
    tex.colorSpace = THREE.SRGBColorSpace
    tex.wrapS = THREE.RepeatWrapping
    tex.wrapT = THREE.RepeatWrapping
    tex.magFilter = THREE.LinearFilter
    tex.minFilter = THREE.LinearMipmapLinearFilter
    tex.generateMipmaps = true
    tex.anisotropy = 16
    tex.needsUpdate = true
    return tex
  }

  private getFallback(): StoreEntry {
    if (this.fallback) return this.fallback
    // 2x2 magenta so missing textures are obvious without crashing.
    const rgba = new Uint8Array([
      255, 0, 220, 255, 30, 30, 30, 255, 30, 30, 30, 255, 255, 0, 220, 255,
    ])
    const texture = this.makeTexture({
      name: 'missing',
      width: 2,
      height: 2,
      rgba,
      transparent: false,
    })
    this.fallback = {
      texture,
      transparent: false,
      width: 16,
      height: 16,
      missing: true,
    }
    return this.fallback
  }

  get(name: string): StoreEntry {
    const key = name.toLowerCase()
    const cached = this.entries.get(key)
    if (cached !== undefined) return cached ?? this.getFallback()

    const dec = this.resolve(name)
    if (!dec) {
      this.entries.set(key, null)
      return this.getFallback()
    }

    const entry: StoreEntry = {
      texture: this.makeTexture(dec),
      transparent: dec.transparent,
      width: dec.width,
      height: dec.height,
      missing: false,
    }
    this.entries.set(key, entry)
    return entry
  }
}

function upscaleTexture(dec: DecodedTexture): DecodedTexture {
  const largest = Math.max(dec.width, dec.height)
  if (largest >= UPSCALE_TARGET) return dec

  const scale = Math.min(
    Math.ceil(UPSCALE_TARGET / largest),
    Math.floor(UPSCALE_MAX / largest),
  )
  if (scale <= 1) return dec

  const width = dec.width * scale
  const height = dec.height * scale
  const rgba = new Uint8Array(width * height * 4)

  for (let y = 0; y < height; y++) {
    const sy = (y + 0.5) / scale - 0.5
    const y0 = wrapIndex(Math.floor(sy), dec.height)
    const y1 = wrapIndex(y0 + 1, dec.height)
    const fy = THREE.MathUtils.smoothstep(sy - Math.floor(sy), 0, 1)

    for (let x = 0; x < width; x++) {
      const sx = (x + 0.5) / scale - 0.5
      const x0 = wrapIndex(Math.floor(sx), dec.width)
      const x1 = wrapIndex(x0 + 1, dec.width)
      const fx = THREE.MathUtils.smoothstep(sx - Math.floor(sx), 0, 1)

      const dst = (y * width + x) * 4
      for (let c = 0; c < 4; c++) {
        const a = dec.rgba[(y0 * dec.width + x0) * 4 + c]
        const b = dec.rgba[(y0 * dec.width + x1) * 4 + c]
        const c0 = dec.rgba[(y1 * dec.width + x0) * 4 + c]
        const d = dec.rgba[(y1 * dec.width + x1) * 4 + c]
        const top = THREE.MathUtils.lerp(a, b, fx)
        const bottom = THREE.MathUtils.lerp(c0, d, fx)
        rgba[dst + c] = Math.round(THREE.MathUtils.lerp(top, bottom, fy))
      }
    }
  }

  return {
    ...dec,
    width,
    height,
    rgba,
  }
}

function wrapIndex(v: number, size: number): number {
  return ((v % size) + size) % size
}
